import React, { createContext, useCallback, useEffect, useState } from 'react';
import { api } from '../api';

// Permette al resto dell'app di sapere se il login è attivo e di uscire.
// In locale `authRequired` è falso e il pulsante "Esci" non compare.
export const AuthContext = createContext({ authRequired: false, logout: () => {} });

const COLORI = {
  sfondo: '#0a0c10', pannello: '#0d1117', bordo: '#1e2736',
  testo: '#e2e8f0', spento: '#64748b', accento: '#60a5fa',
  campo: '#131720', errore: '#f87171',
};

/** Cancello dell'interfaccia: finché non c'è sessione, mostra solo la password. */
export default function LoginGate({ children }) {
  const [stato, setStato] = useState('verifica');   // verifica | dentro | fuori
  const [authRequired, setAuthRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState('');
  const [attesa, setAttesa] = useState(false);

  const verifica = useCallback(async () => {
    try {
      const r = await api.auth.me();
      setAuthRequired(!!r.auth_required);
      setStato(r.authenticated ? 'dentro' : 'fuori');
    } catch {
      // Se non riusciamo nemmeno a chiedere, meglio mostrare il login che
      // un'app vuota senza spiegazione.
      setAuthRequired(true);
      setStato('fuori');
    }
  }, []);

  useEffect(() => { verifica(); }, [verifica]);

  // Se la sessione scade mentre l'app è aperta, si torna qui.
  useEffect(() => {
    const alScadere = () => setStato('fuori');
    window.addEventListener('psa:sessione-scaduta', alScadere);
    return () => window.removeEventListener('psa:sessione-scaduta', alScadere);
  }, []);

  const entra = async (e) => {
    e.preventDefault();
    if (!password || attesa) return;
    setAttesa(true); setErrore('');
    try {
      await api.auth.login(password);
      setPassword('');
      setStato('dentro');
    } catch (err) {
      setErrore(err.message || 'Password errata');
    } finally {
      setAttesa(false);
    }
  };

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } catch {}
    setStato('fuori');
  }, []);

  if (stato === 'verifica') {
    return (
      <div style={{ minHeight: '100vh', background: COLORI.sfondo, color: COLORI.spento,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
        Caricamento…
      </div>
    );
  }

  if (stato === 'fuori') {
    return (
      <div style={{ minHeight: '100vh', background: COLORI.sfondo, color: COLORI.testo,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, fontFamily: "'Inter', sans-serif" }}>
        <form onSubmit={entra} style={{
          width: '100%', maxWidth: 340, background: COLORI.pannello,
          border: `1px solid ${COLORI.bordo}`, borderRadius: 12, padding: 28,
        }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 18,
            fontWeight: 600, color: COLORI.accento, marginBottom: 4 }}>PSA</div>
          <div style={{ fontSize: 12, color: COLORI.spento, marginBottom: 22 }}>
            Personal Share Analyst
          </div>

          <label htmlFor="psa-password" style={{ display: 'block', fontSize: 12,
            color: COLORI.spento, marginBottom: 7 }}>Password</label>
          <input
            id="psa-password"
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); setErrore(''); }}
            autoFocus
            autoComplete="current-password"
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 14, padding: '11px 12px',
              background: COLORI.campo, border: `1px solid ${errore ? COLORI.errore : COLORI.bordo}`,
              borderRadius: 8, color: COLORI.testo, outline: 'none',
            }}
          />

          {errore && (
            <div role="alert" style={{ fontSize: 12, color: COLORI.errore, marginTop: 9 }}>
              {errore}
            </div>
          )}

          <button type="submit" disabled={attesa || !password} style={{
            width: '100%', marginTop: 18, fontSize: 14, fontWeight: 500, padding: '11px 12px',
            background: (attesa || !password) ? '#16202e' : '#1e3a5f',
            color: (attesa || !password) ? COLORI.spento : COLORI.accento,
            border: `1px solid ${(attesa || !password) ? COLORI.bordo : '#2563eb40'}`,
            borderRadius: 8, cursor: (attesa || !password) ? 'default' : 'pointer',
          }}>
            {attesa ? 'Verifica…' : 'Entra'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ authRequired, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
